/**
 * Chakra 2 — Svadhisthana (§7). Element Water, kleur oranje, mantra VAM (417Hz).
 * Resource-label: Flow. Wereld: "De Bevroren Rivier".
 *
 * Bevat de optionele 21e kaart (Oranje Ademhaling) die het spec uitdrukkelijk
 * toestaat. Morveth's leermoment staat in het spec en is gevuld; de sub-baas-
 * leermomenten + monologen volgen in de VN-fase.
 */
import type { Chakra } from './types';
import { HEADERS } from './headers';

export const chakra2: Chakra = {
  header: HEADERS[2],
  wereld: 'De Bevroren Rivier',
  codex:
    'Svadhisthana, "zoete verblijfplaats", zit ter hoogte van het heiligbeen/onderbuik. Element Water, kleur oranje, 6 bloemblaadjes, mantra VAM (417Hz). Levensfase: vroege kindertijd, emotie en plezier ontdekken. Levensvraag: mag ik voelen wat ik voel? Lichamelijk: bekken, voortplantingsorganen, blaas, nieren. Tekort: emotioneel afgesneden, schaamte, rigide, plezier voelt onveilig of "verkeerd". Teveel: emotioneel overspoeld, grenzeloos, verslavingsgevoeligheid, alles draait om gevoel zonder structuur. Balans: vrije, gezonde emotionele flow, creativiteit, gezond genot, intimiteit zonder schaamte. Herstel: dans, water, creatieve expressie, huilen waar het veilig is.',

  kaarten: [
    { id: 'ch2-maansteen', naam: 'Maansteen', type: 's', cost: 1, origin: 2, tekst: '+3 flow, neutraliseert schaamteaanvallen', effect: { block: 3, cleanse: 'one' } },
    { id: 'ch2-oranje-calciet', naam: 'Oranje Calciet', type: 's', cost: 1, origin: 2, tekst: '+3 HP', effect: { heal: 3 } },
    { id: 'ch2-carneool', naam: 'Carneool', type: 's', cost: 2, origin: 2, tekst: 'Praktijkkaarten -1 kosten (2 beurten)', effect: { costDownType: { type: 'p', amt: 1, turns: 2 } } },
    { id: 'ch2-zonnesteen', naam: 'Zonnesteen', type: 's', cost: 2, origin: 2, tekst: 'Trek 2 extra kaarten', effect: { draw: 2 } },
    { id: 'ch2-amber', naam: 'Amber', type: 's', cost: 3, origin: 2, tekst: 'Verwijder alle bevriezingen', effect: { cleanse: 'all', unfreeze: true } },
    { id: 'ch2-blaas-balans', naam: 'Blaas Balans', type: 'o', cost: 1, origin: 2, tekst: 'Neutraliseert energieverlies-aanvallen', effect: { cleanse: 'one' } },
    { id: 'ch2-reproductief-systeem', naam: 'Reproductief Systeem', type: 'o', cost: 2, origin: 2, tekst: '+4 HP, +1 energie', effect: { heal: 4, energy: 1 } },
    { id: 'ch2-heupopening', naam: 'Heupopening', type: 'o', cost: 1, origin: 2, tekst: 'Bevrijdt bevroren kaarten', effect: { unfreeze: true } },
    { id: 'ch2-niergloed', naam: 'Niergloed', type: 'o', cost: 2, origin: 2, tekst: '+3 energie volgende beurt', effect: { energyNext: 3 } },
    { id: 'ch2-dans', naam: 'Dans', type: 'p', cost: 2, origin: 2, tekst: '+5 schade aan baas, bevrijdt emoties', effect: { dmg: 5, cleanse: 'one' } },
    { id: 'ch2-water-therapie', naam: 'Water Therapie', type: 'p', cost: 1, origin: 2, tekst: '+4 HP, neutraliseert bevriezing', effect: { heal: 4, cleanse: 'one' } },
    { id: 'ch2-creatief-schrijven', naam: 'Creatief Schrijven', type: 'p', cost: 1, origin: 2, tekst: 'Trek 3 kaarten', effect: { draw: 3 } },
    { id: 'ch2-heupbewegingen', naam: 'Heupbewegingen', type: 'p', cost: 2, origin: 2, tekst: 'Zolang actief: +2 flow/beurt', effect: { aura: { block: 2, name: 'Heupbewegingen' } } },
    { id: 'ch2-spelen', naam: 'Spelen', type: 'p', cost: 1, origin: 2, tekst: '+2 energie, +2 HP', effect: { energy: 2, heal: 2 } },
    { id: 'ch2-water-element', naam: 'Water Element', type: 'e', cost: 3, origin: 2, tekst: 'Alle Svadhisthana-kaarten ×2', effect: { mult: { origin: 2, amt: 2 } } },
    { id: 'ch2-stromend-water', naam: 'Stromend Water', type: 'e', cost: 2, origin: 2, tekst: 'Verwijder alle blokkades + bevriezingen', effect: { cleanse: 'all', unfreeze: true } },
    { id: 'ch2-levende-stroom', naam: 'Levende Stroom', type: 'e', cost: 2, origin: 2, tekst: 'Verwijder alle bevriezingen, +5 flow', effect: { cleanse: 'all', unfreeze: true, block: 5 }, meesterschap: true },
    { id: 'ch2-vam-mantra', naam: 'VAM Mantra', type: 'm', cost: 2, origin: 2, tekst: 'Ontdooi 1 kaart, +3 flow', effect: { unfreeze: true, block: 3, tone: 417 } },
    { id: 'ch2-vam-verdiept', naam: 'VAM Verdiept', type: 'm', cost: 3, origin: 2, tekst: 'Alle bevriezingen weg, +5 HP', effect: { cleanse: 'all', unfreeze: true, heal: 5, tone: 417 } },
    { id: 'ch2-oranje-visualisatie', naam: 'Oranje Visualisatie', type: 'k', cost: 1, origin: 2, tekst: '+2 energie, creatieve kaarten +1', effect: { energy: 2, buffNext: 1 } },
    { id: 'ch2-oranje-ademhaling', naam: 'Oranje Ademhaling', type: 'k', cost: 2, origin: 2, tekst: 'Trek 2 kaarten, +2 HP', effect: { draw: 2, heal: 2 } },
  ],

  bazen: [
    {
      id: 'ch2-shaemu',
      naam: 'Shaemu',
      titel: 'De Inprenter',
      hp: 22,
      energie: 2,
      aanvallen: [
        { naam: 'Dat Doe Je Niet', cost: 2, effect: { status: { kind: 'costUp', type: 'p', amt: 2, turns: 1 } } },
        { naam: 'Smerig', cost: 1, effect: { dmg: 3, energyLoss: 1 } },
        { naam: 'Wat Zullen Ze Denken', cost: 3, effect: { status: { kind: 'lockType', type: 'p', turns: 2 } } },
      ],
      zwaktes: [
        { card: 'Dans', bonusDmg: 3 },
        { card: 'Water Element', counters: 'Smerig' },
        { card: 'Maansteen', bossEnergyLoss: 2 },
      ],
    },
    {
      id: 'ch2-rigoth',
      naam: 'Rigoth',
      titel: 'De Verstijfde',
      hp: 28,
      energie: 2,
      aanvallen: [
        { naam: 'Gevoelsblokkade', cost: 2, effect: { status: { kind: 'nullifyHeal', turns: 1 } } },
        { naam: 'Verstarring', cost: 3, effect: { energyLoss: 4 } },
        { naam: 'Emotionele Muur', cost: 2, effect: { status: { kind: 'costUp', type: 'e', amt: 2, turns: 2 } } },
      ],
      zwaktes: [
        { card: 'Maansteen', counters: 'Emotionele Muur' },
        { card: 'Stromend Water', bonusDmg: 4 },
        { card: 'VAM Mantra', counters: 'Verstarring' },
      ],
    },
    {
      id: 'ch2-velorn',
      naam: 'Velorn',
      titel: 'De Leeggezogen',
      hp: 32,
      energie: 3,
      aanvallen: [
        { naam: 'Levenskracht Onttrekken', cost: 3, effect: { status: { kind: 'energyDrainPerTurn', amt: 2, turns: 3 } } },
        { naam: 'Creatieve Droogte', cost: 2, effect: { status: { kind: 'drawLess', amt: 1, turns: 2 } } },
        { naam: 'Genot Vergiftigen', cost: 2, effect: { status: { kind: 'debuffOutput', amt: 2, turns: 1 } } },
      ],
      zwaktes: [
        { card: 'Oranje Calciet', bonusHeal: 3 },
        { card: 'Creatief Schrijven', bonusHeal: 6 },
        { card: 'Water Element', counters: '*' },
      ],
    },
    {
      id: 'ch2-morveth',
      naam: 'Morveth',
      titel: 'De Bevroren Bron',
      hp: 55,
      energie: 4,
      eindbaas: true,
      passief: '40% kans per beurt dat een willekeurige handkaart bevriest (1 beurt).',
      aanvallen: [
        { naam: 'IJsgreep', cost: 3, effect: { freeze: 2 } },
        { naam: 'Totale Gevoelloosheid', cost: 4, effect: { status: { kind: 'nullifyAll', turns: 1 } } },
        { naam: 'Diepvries', cost: 3, effect: { status: { kind: 'costUp', type: 'e', amt: 3, turns: 2 } } },
        { naam: 'Leegte Uitstraling', cost: 2, effect: { dmg: 6 } },
      ],
      combo: { bossHpLoss: 20 },
      zwaktes: [
        { card: 'VAM Mantra', counters: 'IJsgreep' },
        { card: 'Dans', counters: 'Totale Gevoelloosheid' },
      ],
      unlock: 'ch2-levende-stroom',
      leermoment:
        'Morveth toont de uiterste prijs van bevriezing: totale gevoelloosheid is geen bescherming, het is een dood die nog ademt. Voelen doet pijn, maar niet-voelen verstikt alles wat leeft. Herstel: kleine emoties toelaten, dansen, huilen waar het veilig is, je lichaam laten ontdooien in jouw eigen tempo.',
    },
  ],
};
